import { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Button from 'common/components/button/Button';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import TextInput from 'common/components/textInput/TextInput';
import { Header, Label } from 'common/components/texts/Texts';
import ExpressionsService, { ExpressionParameter, ExpressionResult } from 'api/expressions/ExpressionsService';
import ExpressionSystem from 'common/components/expressionButton/ExpressionSystem';
import Loading from 'common/services/Loading';
import { FaPlus, FaTrash } from 'react-icons/fa';
import MoneyInput from 'common/components/moneyInput/MoneyInput';
import styles from './ExpressionTestScreen.module.scss';

function ExpressionTestScreen(): JSX.Element {
    const [expression, setExpression] = useState<string>('100 * 2 + 50');
    const [parameters, setParameters] = useState<ExpressionParameter[]>([]);

    const [frontendResult, setFrontendResult] = useState<{ value: number; error: boolean; errorMessage?: string } | null>(null);
    const [backendResult, setBackendResult] = useState<ExpressionResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const addParameter = () => {
        setParameters([
            ...parameters,
            { id: `param${parameters.length + 1}`, name: `Parameter ${parameters.length + 1}`, value: 0 }
        ]);
    };

    const removeParameter = (index: number) => {
        setParameters(parameters.filter((_, i) => i !== index));
    };

    const updateParameter = (index: number, field: keyof ExpressionParameter, value: string | number | null) => {
        const updated = [...parameters];
        updated[index] = { ...updated[index], [field]: value };
        setParameters(updated);
    };

    const evaluateFrontend = () => {
        const result = ExpressionSystem.evaluate(expression, parameters.map(p => ({
            id: p.id,
            name: p.name,
            value: p.value
        })));
        setFrontendResult(result);
    };

    const evaluateBackend = async () => {
        setIsLoading(true);
        Loading.show();
        try {
            const result = await ExpressionsService.evaluate({
                expression,
                parameters
            });
            setBackendResult(result);
        } catch (error) {
            console.error('Backend evaluation error:', error);
            setBackendResult({ value: 0, error: true, errorMessage: String(error) });
        } finally {
            setIsLoading(false);
            Loading.hide();
        }
    };

    const evaluateBoth = async () => {
        evaluateFrontend();
        await evaluateBackend();
    };

    return (
        <ScreenTitle title={'Expression Test'}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: 'Home', url: '/' },
                    { name: 'Expression Test', url: '/test-expressions' },
                ]}
            />

            <PageHeader title={'Expression Evaluation Test'} informationText={'Compare Frontend vs Backend'} />

            <PageContainer>
                <Row className="mb-4">
                    <Col>
                        <Label>Expression</Label>
                        <TextInput
                            placeholder="Enter expression (e.g., 100 * 2 + M_price_E)"
                            value={expression}
                            onChange={(e) => setExpression(e.target.value ?? '')}
                        />
                        <small className="text-muted">
                            Use M_parameterId_E to reference parameters (e.g., M_price_E for parameter with id &quot;price&quot;)
                        </small>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Header>Parameters</Header>
                            <Button size="small" onClick={addParameter}>
                                <FaPlus /> Add Parameter
                            </Button>
                        </div>

                        {parameters.length === 0 && (
                            <p className="text-muted">No parameters added. Click &quot;Add Parameter&quot; to add one.</p>
                        )}

                        {parameters.map((param, index) => (
                            <Row key={index} className="mb-2 align-items-end">
                                <Col xs={3}>
                                    <Label>ID</Label>
                                    <TextInput
                                        value={param.id}
                                        onChange={(e) => updateParameter(index, 'id', e.target.value ?? '')}
                                    />
                                </Col>
                                <Col xs={4}>
                                    <Label>Name</Label>
                                    <TextInput
                                        value={param.name}
                                        onChange={(e) => updateParameter(index, 'name', e.target.value ?? '')}
                                    />
                                </Col>
                                <Col xs={3}>
                                    <Label>Value {Array.isArray(param.value) && '(Array)'}</Label>
                                    {Array.isArray(param.value)
                                        ? (
                                            <TextInput
                                                value={param.value.join(', ')}
                                                onChange={(e) => {
                                                    const val = e.target.value ?? '';
                                                    const arr = val.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
                                                    updateParameter(index, 'value', arr.length > 0 ? arr as unknown as number : null);
                                                }}
                                                placeholder="Comma-separated values"
                                            />
                                        )
                                        : (
                                            <MoneyInput
                                                value={param.value}
                                                onChange={(v) => updateParameter(index, 'value', v ?? null)}
                                                unitPrefix=""
                                            />
                                        )}
                                </Col>
                                <Col xs={2}>
                                    <Button variant="danger" size="small" onClick={() => removeParameter(index)}>
                                        <FaTrash />
                                    </Button>
                                </Col>
                            </Row>
                        ))}
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col>
                        <Button onClick={evaluateBoth} disabled={isLoading}>
                            Evaluate Both
                        </Button>
                        <Button variant="secondary" onClick={evaluateFrontend} disabled={isLoading} className="ms-2">
                            Frontend Only
                        </Button>
                        <Button variant="secondary" onClick={evaluateBackend} disabled={isLoading} className="ms-2">
                            Backend Only
                        </Button>
                    </Col>
                </Row>

                <Row>
                    <Col xs={6}>
                        <div className={styles.resultBox}>
                            <Header>Frontend Result (ExpressionSystem.ts)</Header>
                            {frontendResult && (
                                <div className={frontendResult.error ? styles.error : styles.success}>
                                    <p><strong>Value:</strong> {frontendResult.value}</p>
                                    <p><strong>Error:</strong> {frontendResult.error ? 'Yes' : 'No'}</p>
                                    {frontendResult.errorMessage && (
                                        <p><strong>Message:</strong> {frontendResult.errorMessage}</p>
                                    )}
                                </div>
                            )}
                            {!frontendResult && <p className="text-muted">Not evaluated yet</p>}
                        </div>
                    </Col>
                    <Col xs={6}>
                        <div className={styles.resultBox}>
                            <Header>Backend Result (V8ExpressionService)</Header>
                            {backendResult && (
                                <div className={backendResult.error ? styles.error : styles.success}>
                                    <p><strong>Value:</strong> {backendResult.value}</p>
                                    <p><strong>Error:</strong> {backendResult.error ? 'Yes' : 'No'}</p>
                                    {backendResult.errorMessage && (
                                        <p><strong>Message:</strong> {backendResult.errorMessage}</p>
                                    )}
                                </div>
                            )}
                            {!backendResult && <p className="text-muted">Not evaluated yet</p>}
                        </div>
                    </Col>
                </Row>

                {frontendResult && backendResult && (
                    <Row className="mt-4">
                        <Col>
                            <div className={
                                frontendResult.value === backendResult.value && frontendResult.error === backendResult.error
                                    ? styles.matchSuccess
                                    : styles.matchError
                            }>
                                {frontendResult.value === backendResult.value && frontendResult.error === backendResult.error
                                    ? 'Results MATCH! Frontend and Backend produce the same result.'
                                    : 'Results DO NOT MATCH! There is a discrepancy between Frontend and Backend.'
                                }
                            </div>
                        </Col>
                    </Row>
                )}

                <Row className="mt-4">
                    <Col>
                        <Header>Quick Test Examples</Header>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button size="small" variant="third" onClick={() => setExpression('100 * 2 + 50')}>
                                Simple: 100 * 2 + 50
                            </Button>
                            <Button size="small" variant="third" onClick={() => setExpression('SQRT(16) + POWER(2, 3)')}>
                                Functions: SQRT(16) + POWER(2, 3)
                            </Button>
                            <Button size="small" variant="third" onClick={() => {
                                setExpression('M_price_E * M_quantity_E');
                                setParameters([
                                    { id: 'price', name: 'Price', value: 100 },
                                    { id: 'quantity', name: 'Quantity', value: 5 }
                                ]);
                            }}>
                                With Params: price * quantity
                            </Button>
                            <Button size="small" variant="third" onClick={() => setExpression('SUM(1, 2, 3, 4, 5)')}>
                                SUM: SUM(1, 2, 3, 4, 5)
                            </Button>
                            <Button size="small" variant="third" onClick={() => setExpression('IF(10 > 5, 100, 200)')}>
                                IF: IF(10 &gt; 5, 100, 200)
                            </Button>
                            <Button size="small" variant="third" onClick={() => {
                                setExpression('AVERAGE(M_prices_E)');
                                setParameters([
                                    { id: 'prices', name: 'Prices', value: [100, 200, 300, 400, 500] }
                                ]);
                            }}>
                                AVERAGE: Array param
                            </Button>
                            <Button size="small" variant="third" onClick={() => {
                                setExpression('SUM(M_values_E) * 1.1');
                                setParameters([
                                    { id: 'values', name: 'Values', value: [10, 20, 30, 40] }
                                ]);
                            }}>
                                SUM: Array * 1.1
                            </Button>
                        </div>
                    </Col>
                </Row>
            </PageContainer>
        </ScreenTitle>
    );
}

export default ExpressionTestScreen;
